<template>
  <div id="salary-info">
    <v-card class="card-shadow border-radius-xl mt-4">
      <v-card-title class="pb-0 d-flex align-center">
        <h6 class="mb-0 text-h6 text-typo font-weight-bold">薪水資訊</h6>
        <v-spacer></v-spacer>
        <template v-if="canEditRole && !isCreateMode">
          <template v-if="!edit">
            <v-btn icon @click="startEdit" :ripple="false">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon @click="showHistory" :ripple="false">
              <v-icon>mdi-history</v-icon>
            </v-btn>
          </template>
          <template v-else>
            <v-btn icon class="mr-2" @click="cancelEdit" :ripple="false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <v-btn icon :loading="saving" @click="submit" :ripple="false">
              <v-icon color="red">mdi-content-save</v-icon>
            </v-btn>
          </template>
        </template>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="3">
            <v-list-item class="px-0">
              <v-list-item-content>
                <v-list-item-subtitle class="text-sm text-body"
                  >底薪</v-list-item-subtitle
                >
                <v-list-item-title v-if="!edit" class="text-body-1 text-typo">{{
                  formatCurrency(profileData.basic_salary)
                }}</v-list-item-title>
                <v-text-field
                  v-else
                  v-model.number="form.basic_salary"
                  type="number"
                  min="0"
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-list-item-content>
            </v-list-item>
          </v-col>
          <v-col cols="12" md="3">
            <v-list-item class="px-0">
              <v-list-item-content>
                <v-list-item-subtitle class="text-sm text-body"
                  >主管加給</v-list-item-subtitle
                >
                <v-list-item-title v-if="!edit" class="text-body-1 text-typo">{{
                  profileData.supervisor_allowance || "0"
                }}</v-list-item-title>
                <v-text-field
                  v-else
                  v-model="form.supervisor_allowance"
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-list-item-content>
            </v-list-item>
          </v-col>
          <v-col cols="12" md="3">
            <v-list-item class="px-0">
              <v-list-item-content>
                <v-list-item-subtitle class="text-sm text-body"
                  >職務加給</v-list-item-subtitle
                >
                <v-list-item-title v-if="!edit" class="text-body-1 text-typo">{{
                  profileData.position_allowance || "0"
                }}</v-list-item-title>
                <v-text-field
                  v-else
                  v-model.number="form.position_allowance"
                  type="number"
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-list-item-content>
            </v-list-item>
          </v-col>
          <v-col cols="12" md="3">
            <v-list-item class="px-0">
              <v-list-item-content>
                <v-list-item-subtitle class="text-sm text-body"
                  >伙食津貼</v-list-item-subtitle
                >
                <v-list-item-title v-if="!edit" class="text-body-1 text-typo">{{
                  profileData.food_allowance || "0"
                }}</v-list-item-title>
                <v-text-field
                  v-else
                  v-model="form.food_allowance"
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-list-item-content>
            </v-list-item>
          </v-col>
          <!-- 績效獎金 bonus -->
          <v-col cols="12" md="3">
            <v-list-item class="px-0">
              <v-list-item-content>
                <v-list-item-subtitle class="text-sm text-body"
                  >績效獎金</v-list-item-subtitle
                >
                <v-list-item-title v-if="!edit" class="text-body-1 text-typo">{{
                  profileData.bonus || "0"
                }}</v-list-item-title>
                <v-text-field
                  v-else
                  v-model.number="form.bonus"
                  type="number"
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-list-item-content>
            </v-list-item>
          </v-col>
          <v-col cols="12" md="3">
            <v-list-item class="px-0">
              <v-list-item-content>
                <v-list-item-subtitle class="text-sm text-body"
                  >自訂加給</v-list-item-subtitle
                >
                <v-tooltip
                  bottom
                  v-if="
                    !edit && Object.keys(mergedCompanyExtraSalary).length > 0
                  "
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-list-item-title
                      class="text-body-1 text-typo"
                      v-bind="attrs"
                      v-on="on"
                    >
                      {{ formatCompanyExtraSalaryString() }}
                    </v-list-item-title>
                  </template>
                  <div
                    v-for="(val, key) in mergedCompanyExtraSalary"
                    :key="key"
                  >
                    {{ key }}: {{ (Number(val) || 0).toLocaleString() }}
                  </div>
                </v-tooltip>
                <v-list-item-title
                  v-else-if="!edit"
                  class="text-body-1 text-typo"
                  >{{ formatCompanyExtraSalaryString() }}</v-list-item-title
                >
                <v-btn
                  v-else
                  small
                  elevation="0"
                  :ripple="false"
                  height="43"
                  class="
                    font-weight-bold
                    text-capitalize
                    btn-default
                    bg-gradient-default
                    py-3
                    px-6
                  "
                  @click="openCompanyExtraSalaryDialog"
                >
                  編輯
                </v-btn>
              </v-list-item-content>
            </v-list-item>
          </v-col>
          <v-col cols="12" md="4">
            <v-list-item class="px-0">
              <v-list-item-content>
                <v-list-item-subtitle class="text-sm text-body"
                  >額外保險</v-list-item-subtitle
                >
                <v-list-item-title v-if="!edit" class="text-body-1 text-typo">{{
                  formatCurrency(getDependentHealthInsuranceTotal())
                }}</v-list-item-title>
                <v-btn
                  v-else
                  small
                  elevation="0"
                  :ripple="false"
                  height="43"
                  class="
                    font-weight-bold
                    text-capitalize
                    btn-primary
                    bg-gradient-default
                    py-3
                    px-6
                  "
                  @click="openDependentHealthDialog"
                >
                  編輯
                </v-btn>
              </v-list-item-content>
            </v-list-item>
          </v-col>

          <v-col cols="12" md="3" v-if="false">
            <v-list-item class="px-0">
              <v-list-item-content>
                <v-list-item-subtitle class="text-sm text-body"
                  >薪資結算日期</v-list-item-subtitle
                >
                <v-list-item-title v-if="!edit" class="text-body-1 text-typo">{{
                  getDayOfMonth(profileData.salary_settle_date)
                }}</v-list-item-title>
                <v-text-field
                  v-else
                  v-model.number="form.salary_settle_date"
                  type="number"
                  min="0"
                  max="31"
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-list-item-content>
            </v-list-item>
          </v-col>
          <v-col cols="12" md="3" v-if="false">
            <v-list-item class="px-0">
              <v-list-item-content>
                <v-list-item-subtitle class="text-sm text-body"
                  >薪資發放頻率</v-list-item-subtitle
                >
                <v-list-item-title v-if="!edit" class="text-body-1 text-typo">{{
                  getFrequency(profileData.salary_pay_freq)
                }}</v-list-item-title>
                <v-text-field
                  v-else
                  v-model.number="form.salary_pay_freq"
                  type="number"
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-list-item-content>
            </v-list-item>
          </v-col>
          <v-col cols="12" md="3" v-if="false">
            <v-list-item class="px-0">
              <v-list-item-content>
                <v-list-item-subtitle class="text-sm text-body"
                  >薪資發放日期</v-list-item-subtitle
                >
                <v-list-item-title v-if="!edit" class="text-body-1 text-typo">{{
                  getDayOfMonth(profileData.salary_pay_date)
                }}</v-list-item-title>
                <v-text-field
                  v-else
                  v-model.number="form.salary_pay_date"
                  type="number"
                  min="0"
                  max="31"
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-list-item-content>
            </v-list-item>
          </v-col>

          <v-col cols="12" md="3">
            <v-list-item class="px-0">
              <v-list-item-content>
                <v-list-item-subtitle class="text-sm text-body"
                  >銀行</v-list-item-subtitle
                >
                <v-list-item-title v-if="!edit" class="text-body-1 text-typo">{{
                  profileData.bank || "未設定"
                }}</v-list-item-title>
                <v-text-field
                  v-else
                  v-model="form.bank"
                  maxlength="100"
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-list-item-content>
            </v-list-item>
          </v-col>
          <v-col cols="12" md="3">
            <v-list-item class="px-0">
              <v-list-item-content>
                <v-list-item-subtitle class="text-sm text-body"
                  >銀行分行</v-list-item-subtitle
                >
                <v-list-item-title v-if="!edit" class="text-body-1 text-typo">{{
                  profileData.branch || "未設定"
                }}</v-list-item-title>
                <v-text-field
                  v-else
                  v-model="form.branch"
                  maxlength="100"
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-list-item-content>
            </v-list-item>
          </v-col>
          <v-col cols="12" md="3">
            <v-list-item class="px-0">
              <v-list-item-content>
                <v-list-item-subtitle class="text-sm text-body"
                  >薪資帳號</v-list-item-subtitle
                >
                <v-list-item-title v-if="!edit" class="text-body-1 text-typo">{{
                  profileData.salary_account || "未設定"
                }}</v-list-item-title>
                <v-text-field
                  v-else
                  v-model="form.salary_account"
                  maxlength="100"
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-list-item-content>
            </v-list-item>
          </v-col>
          <v-col cols="12" md="3">
            <v-list-item class="px-0">
              <v-list-item-content>
                <v-list-item-subtitle class="text-sm text-body"
                  >備註</v-list-item-subtitle
                >
                <v-list-item-title v-if="!edit" class="text-body-1 text-typo">{{
                  profileData.note || "未設定"
                }}</v-list-item-title>
                <v-text-field
                  v-else
                  v-model="form.note"
                  maxlength="1000"
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-list-item-content>
            </v-list-item>
          </v-col>
          <v-col cols="12" md="3">
            <v-list-item class="px-0">
              <v-list-item-content>
                <v-list-item-subtitle class="text-sm text-body"
                  >勞工自提勞退</v-list-item-subtitle
                >
                <v-list-item-title v-if="!edit" class="text-body-1 text-typo">{{
                  profileData.employee_voluntary_pension ? "是" : "否"
                }}</v-list-item-title>
                <v-checkbox
                  v-else
                  v-model="form.employee_voluntary_pension"
                  dense
                  hide-details="auto"
                ></v-checkbox>
              </v-list-item-content>
            </v-list-item>
          </v-col>
          <!-- add employee_voluntary_pension_rate -->
          <v-col cols="12" md="3">
            <v-list-item class="px-0">
              <v-list-item-content>
                <v-list-item-subtitle class="text-sm text-body"
                  >勞工自提勞退率%</v-list-item-subtitle
                >
                <v-list-item-title v-if="!edit" class="text-body-1 text-typo">{{
                  profileData.employee_voluntary_pension
                    ? profileData.employee_voluntary_pension_rate || "未設定"
                    : "未設定"
                }}</v-list-item-title>
                <v-text-field
                  v-else
                  v-model.number="form.employee_voluntary_pension_rate"
                  type="number"
                  min="1"
                  max="6"
                  :rules="[
                    (v) => !v || (v >= 1 && v <= 6) || '必須介於 1 到 6 之間',
                  ]"
                  :disabled="!form.employee_voluntary_pension"
                  dense
                  hide-details="auto"
                ></v-text-field>
              </v-list-item-content>
            </v-list-item>
          </v-col>
          <v-col cols="12" md="3">
            <v-list-item class="px-0">
              <v-list-item-content>
                <v-list-item-subtitle class="text-sm text-body"
                  >眷屬加保人數</v-list-item-subtitle
                >
                <v-list-item-title v-if="!edit" class="text-body-1 text-typo">
                  {{ form.dependent_insured_count || 0 }} 人
                </v-list-item-title>
                <v-text-field
                  v-else
                  v-model.number="form.dependent_insured_count"
                  type="number"
                  min="0"
                  dense
                  hide-details="auto"
                  @blur="normalizeDependentCount"
                ></v-text-field>
              </v-list-item-content>
            </v-list-item>
          </v-col>
          <v-col cols="12" md="3">
            <v-list-item class="px-0">
              <v-list-item-content>
                <v-list-item-subtitle class="text-sm text-body"
                  >勞保級距上限</v-list-item-subtitle
                >
                <v-list-item-title v-if="!edit" class="text-body-1 text-typo">{{
                  profileData.insurance_bracket_max != null
                    ? profileData.insurance_bracket_max
                    : "未設定"
                }}</v-list-item-title>
                <v-autocomplete
                  v-else
                  v-model.number="form.insurance_bracket_max"
                  :items="insuranceBracketOptions"
                  item-text="text"
                  item-value="value"
                  dense
                  hide-details="auto"
                  clearable
                  :loading="bracketLoading"
                  :filter="(item, queryText) => item.text.includes(queryText)"
                  :menu-props="{ contentClass: 'bracket-autocomplete-menu' }"
                  :rules="[
                    (v) =>
                      v === null ||
                      v === undefined ||
                      v === '' ||
                      (Number(v) > 0 && Number.isInteger(Number(v))) ||
                      '必須為正整數',
                  ]"
                >
                  <template v-slot:selection="{ item }">
                    <span v-if="item && item.displayMax">
                      {{ item.text }} (投保金額: {{ item.displayMax }})
                    </span>
                    <span v-else>{{
                      item && item.text ? item.text : item
                    }}</span>
                  </template>
                  <template v-slot:item="{ item: option, on, attrs }">
                    <v-list-item
                      v-bind="attrs"
                      v-on="on"
                      :class="[
                        'bracket-option-item',
                        {
                          'bracket-option-selected': isBracketOptionSelected(
                            option.value,
                            form.insurance_bracket_max
                          ),
                        },
                      ]"
                      :data-bracket-option-selected="
                        isBracketOptionSelected(
                          option.value,
                          form.insurance_bracket_max
                        )
                          ? 'true'
                          : 'false'
                      "
                    >
                      <v-list-item-content>
                        <v-list-item-title>{{ option.text }}</v-list-item-title>
                        <v-list-item-subtitle
                          v-if="option.displayMax"
                          class="text-caption"
                          >投保金額:
                          {{ option.displayMax }}</v-list-item-subtitle
                        >
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </v-list-item-content>
            </v-list-item>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-dialog v-model="dependentHealthDialog" max-width="720">
      <v-card>
        <v-card-title class="pb-0 d-flex align-center">
          <span class="text-h6">額外保險</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeDependentHealthDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" class="d-flex justify-end pb-0">
              <v-btn
                color="blue"
                dark
                class="white--text"
                @click="addDependentHealthItem"
              >
                新增
              </v-btn>
            </v-col>
            <v-col cols="12">
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th class="text-left">項目</th>
                    <th class="text-left">金額</th>
                    <th class="text-left"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, idx) in dependentHealthDialogItems"
                    :key="`dep-health-${idx}`"
                  >
                    <td style="width: 55%">
                      <v-text-field
                        v-model="row.key"
                        dense
                        hide-details="auto"
                      ></v-text-field>
                    </td>
                    <td style="width: 35%">
                      <v-text-field
                        v-model.number="row.value"
                        type="number"
                        min="0"
                        dense
                        hide-details="auto"
                      ></v-text-field>
                    </td>
                    <td style="width: 10%">
                      <v-btn icon @click="removeDependentHealthItem(idx)">
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDependentHealthDialog">取消</v-btn>
          <v-btn
            elevation="0"
            :ripple="false"
            height="43"
            :loading="exporting"
            class="
              font-weight-bold
              text-capitalize
              btn-ls btn-primary
              bg-gradient-primary
              py-3
              px-6
              ms-3
            "
            @click="confirmDependentHealthDialog"
          >
            確認
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="companyExtraSalaryDialog" max-width="720">
      <v-card>
        <v-card-title class="pb-0 d-flex align-center">
          <span class="text-h6">自訂加給</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeCompanyExtraSalaryDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" class="d-flex justify-end pb-0">
              <v-btn
                color="blue"
                dark
                class="white--text"
                @click="addCompanyExtraSalaryItem"
              >
                新增
              </v-btn>
            </v-col>
            <v-col cols="12">
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th class="text-left">加給名稱</th>
                    <th class="text-left">金額</th>
                    <th class="text-left"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, idx) in companyExtraSalaryItems"
                    :key="`comp-extra-${idx}`"
                  >
                    <td style="width: 55%">
                      <v-text-field
                        v-model="row.key"
                        dense
                        hide-details="auto"
                      ></v-text-field>
                    </td>
                    <td style="width: 35%">
                      <v-text-field
                        v-model.number="row.value"
                        type="number"
                        min="0"
                        dense
                        hide-details="auto"
                      ></v-text-field>
                    </td>
                    <td style="width: 10%">
                      <v-btn icon @click="removeCompanyExtraSalaryItem(idx)">
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeCompanyExtraSalaryDialog">取消</v-btn>
          <v-btn
            elevation="0"
            :ripple="false"
            height="43"
            :loading="exporting"
            class="
              font-weight-bold
              text-capitalize
              btn-ls btn-primary
              bg-gradient-primary
              py-3
              px-6
              ms-3
            "
            @click="confirmCompanyExtraSalaryDialog"
          >
            確認
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { SALARY_PAYMENT_FREQUENCY } from "@/constants";
import { formatNumber } from "@/utils/formatters";

export default {
  name: "SalaryInfoWidget",
  props: {
    profileData: {
      type: Object,
      default: () => ({}),
    },
    isCreateMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      exporting: false,
      edit: this.isCreateMode,
      saving: false,
      isInitialized: false,
      dependentHealthDialog: false,
      dependentHealthDialogItems: [],
      companyExtraSalaryDialog: false,
      companyExtraSalaryItems: [],
      insuranceBrackets: [],
      bracketLoading: false,
      form: {
        basic_salary: 0,
        extra_salary: "",
        salary_settle_freq: 0,
        salary_settle_date: 0,
        salary_pay_freq: 0,
        salary_pay_date: 0,
        salary_account: "",
        note: "",
        bank: "",
        branch: "",
        supervisor_allowance: 0,
        position_allowance: 0,
        food_allowance: 0,
        company_extra_salary: {},
        employee_welfare_fund: 0,
        bonus: 0,
        employee_voluntary_pension: false,
        employee_voluntary_pension_rate: 0,
        insurance_bracket_max: null,
        extra_insurance: {},
      },
      extraInsuranceForm: {},
    };
  },
  computed: {
    ...mapGetters({
      canEditRole: "user/canEditRole",
      selectedCompany: "company/selectedCompany",
      userToken: "user/userToken",
    }),
    insuranceBracketOptions() {
      if (!Array.isArray(this.insuranceBrackets)) return [];
      return this.insuranceBrackets
        .filter((b) => b && b.bracket_max != null)
        .map((b) => ({
          text:
            b.bracket_min != null && b.bracket_max != null
              ? `實際工資:${b.bracket_min} ~ ${b.bracket_max}`
              : `${b.bracket_max}`,
          displayMax: b.bracket_max,
          value: Number(b.bracket_max),
        }));
    },
    mergedCompanyExtraSalary() {
      const companyData =
        this.selectedCompany &&
        this.selectedCompany.company_extra_salary &&
        typeof this.selectedCompany.company_extra_salary === "object"
          ? this.selectedCompany.company_extra_salary
          : {};

      const userData = this.form.company_extra_salary;
      const userDataObj =
        userData && typeof userData === "object" ? userData : {};

      return { ...companyData, ...userDataObj };
    },
  },
  watch: {
    profileData: {
      handler() {
        this.resetForm();
      },
      immediate: true,
      deep: true,
    },
    form: {
      handler(newVal) {
        if (this.isCreateMode && this.isInitialized) {
          const payload = {
            ...newVal,
            extra_insurance: JSON.stringify(this.extraInsuranceForm || {}),
          };
          this.$emit("save", payload, () => {});
        }
      },
      deep: true,
    },
    extraInsuranceForm: {
      handler() {
        this.form.extra_insurance = JSON.stringify(this.extraInsuranceForm);
      },
      deep: true,
    },
  },
  mounted() {
    this.fetchInsuranceBrackets();
  },
  methods: {
    formatCurrency(amount) {
      if (!amount && amount !== 0) return "未設定";
      return formatNumber(amount);
    },
    getFrequency(freq) {
      return SALARY_PAYMENT_FREQUENCY[freq] || `Frequency ${freq}`;
    },
    getDayOfMonth(day) {
      if (!day && day !== 0) return "未設定";

      const suffixes = ["th", "st", "nd", "rd"];
      const v = day % 100;
      const suffix = suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0];

      return `${day}${suffix} of the month`;
    },
    getExtraInsuranceValue(key) {
      if (!this.profileData.extra_insurance)
        return key === "dependent_health_insurance" ? 0 : false;
      try {
        const data =
          typeof this.profileData.extra_insurance === "string"
            ? JSON.parse(this.profileData.extra_insurance)
            : this.profileData.extra_insurance;
        return data[key] ?? (key === "dependent_health_insurance" ? 0 : false);
      } catch (e) {
        return key === "dependent_health_insurance" ? 0 : false;
      }
    },
    resetForm() {
      this.form = {
        basic_salary: this.profileData.basic_salary ?? 0,
        extra_salary: this.profileData.extra_salary || "",
        salary_settle_freq: this.profileData.salary_settle_freq ?? 0,
        salary_settle_date: this.profileData.salary_settle_date ?? 0,
        salary_pay_freq: this.profileData.salary_pay_freq ?? 0,
        salary_pay_date: this.profileData.salary_pay_date ?? 0,
        salary_account: this.profileData.salary_account || "",
        note: this.profileData.note || "",
        bank: this.profileData.bank || "",
        branch: this.profileData.branch || "",
        supervisor_allowance: this.profileData.supervisor_allowance || 0,
        position_allowance: this.profileData.position_allowance || 0,
        food_allowance: this.profileData.food_allowance || 0,
        company_extra_salary: this.profileData.company_extra_salary || {},
        employee_welfare_fund: this.profileData.employee_welfare_fund ?? 0,
        bonus: this.profileData.bonus ?? 0,
        employee_voluntary_pension:
          this.profileData.employee_voluntary_pension || false,
        employee_voluntary_pension_rate:
          this.profileData.employee_voluntary_pension_rate || 0,
        insurance_bracket_max: this.profileData.insurance_bracket_max ?? null,
        dependent_insured_count: this.profileData.dependent_insured_count ?? 0,
        extra_insurance: this.profileData.extra_insurance || null,
      };

      let extraInsuranceData = {};
      if (this.profileData.extra_insurance) {
        try {
          extraInsuranceData =
            typeof this.profileData.extra_insurance === "string"
              ? JSON.parse(this.profileData.extra_insurance)
              : this.profileData.extra_insurance;
        } catch (e) {
          console.error("Failed to parse extra_insurance:", e);
        }
      }

      // Handle company_extra_salary parsing if needed
      if (typeof this.form.company_extra_salary === "string") {
        try {
          this.form.company_extra_salary = JSON.parse(
            this.form.company_extra_salary
          );
        } catch (e) {
          this.form.company_extra_salary = {};
        }
      }

      const migratedExtraInsurance = {
        ...(extraInsuranceData && typeof extraInsuranceData === "object"
          ? extraInsuranceData
          : {}),
      };

      if (
        Array.isArray(migratedExtraInsurance.dependent_health_insurance_items)
      ) {
        migratedExtraInsurance.dependent_health_insurance_items.forEach(
          (it) => {
            const k = (it?.key || "").toString().trim() || "團體保險";
            const v = Number(it?.value) || 0;
            if (v > 0) migratedExtraInsurance[k] = v;
          }
        );
      }
      if (migratedExtraInsurance.dependent_health_insurance) {
        const v =
          Number(migratedExtraInsurance.dependent_health_insurance) || 0;
        if (v > 0) migratedExtraInsurance["團體保險"] = v;
      }

      delete migratedExtraInsurance.employee_voluntary_pension;
      delete migratedExtraInsurance.dependent_health_insurance;
      delete migratedExtraInsurance.dependent_health_insurance_items;

      this.extraInsuranceForm = migratedExtraInsurance;

      this.$nextTick(() => {
        this.isInitialized = true;
      });
    },
    getDependentHealthInsuranceTotal() {
      const data = this.extraInsuranceForm;
      if (!data || typeof data !== "object") return 0;
      return Object.values(data).reduce((sum, v) => sum + (Number(v) || 0), 0);
    },
    normalizeDependentCount() {
      let val = Number(this.form.dependent_insured_count) || 0;
      if (val < 0) val = 0;
      this.form.dependent_insured_count = Math.trunc(val);
    },
    openDependentHealthDialog() {
      const data = this.extraInsuranceForm;
      this.dependentHealthDialogItems =
        data && typeof data === "object"
          ? Object.keys(data).map((k) => ({
              key: k,
              value: Number(data[k]) || 0,
            }))
          : [];
      this.dependentHealthDialog = true;
    },
    closeDependentHealthDialog() {
      this.dependentHealthDialog = false;
      this.dependentHealthDialogItems = [];
    },
    async fetchInsuranceBrackets() {
      const currentYear = new Date().getFullYear();
      this.bracketLoading = true;
      try {
        const base = process.env.VUE_APP_API_URL || "";
        const url = `${base}/api/insurance-brackets?year=${encodeURIComponent(
          currentYear
        )}`;

        const res = await fetch(url, {
          method: "GET",
          headers: {
            accept: "application/json",
            ...(this.userToken
              ? { Authorization: `Bearer ${this.userToken}` }
              : {}),
          },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        this.insuranceBrackets = Array.isArray(json?.data) ? json.data : [];
      } catch (error) {
        console.error("Failed to fetch insurance brackets", error);
        this.insuranceBrackets = [];
      } finally {
        this.bracketLoading = false;
      }
    },
    isBracketOptionSelected(optionValue, currentValue) {
      if (optionValue == null || currentValue == null) return false;
      return Number(optionValue) === Number(currentValue);
    },
    addDependentHealthItem() {
      this.dependentHealthDialogItems.push({ key: "團體保險", value: 0 });
    },
    removeDependentHealthItem(idx) {
      this.dependentHealthDialogItems.splice(idx, 1);
    },
    confirmDependentHealthDialog() {
      const map = {};
      this.dependentHealthDialogItems
        .filter(
          (it) =>
            (it?.key || "").toString().trim() !== "" ||
            (Number(it?.value) || 0) > 0
        )
        .forEach((it) => {
          const k = (it?.key || "").toString().trim() || "團體保險";
          const v = Number(it?.value) || 0;
          map[k] = v;
        });
      this.extraInsuranceForm = map;
      this.closeDependentHealthDialog();
    },
    // Company Extra Salary Methods
    formatCompanyExtraSalaryString() {
      const mergedData = this.mergedCompanyExtraSalary;
      const entries = Object.entries(mergedData);
      if (entries.length === 0) return "未設定";

      // If only one item, show the detail
      if (entries.length === 1) {
        const [key, value] = entries[0];
        const val = Number(value) || 0;
        return `${key}:${val.toLocaleString()}`;
      }

      // If more than one item, show summary: "共X筆 $總金額"
      const total = entries.reduce((sum, [, value]) => {
        return sum + (Number(value) || 0);
      }, 0);
      return `共${entries.length}筆 $${total.toLocaleString()}`;
    },
    getCompanyExtraSalaryTotal() {
      const mergedData = this.mergedCompanyExtraSalary;
      if (!mergedData || typeof mergedData !== "object") return 0;
      return Object.values(mergedData).reduce(
        (sum, v) => sum + (Number(v) || 0),
        0
      );
    },
    openCompanyExtraSalaryDialog() {
      const mergedData = this.mergedCompanyExtraSalary;

      this.companyExtraSalaryItems =
        Object.keys(mergedData).length > 0
          ? Object.keys(mergedData).map((k) => ({
              key: k,
              value: Number(mergedData[k]) || 0,
            }))
          : [];
      this.companyExtraSalaryDialog = true;
    },
    closeCompanyExtraSalaryDialog() {
      this.companyExtraSalaryDialog = false;
      this.companyExtraSalaryItems = [];
    },
    addCompanyExtraSalaryItem() {
      this.companyExtraSalaryItems.push({ key: "", value: 0 });
    },
    removeCompanyExtraSalaryItem(idx) {
      this.companyExtraSalaryItems.splice(idx, 1);
    },
    confirmCompanyExtraSalaryDialog() {
      const map = {};
      this.companyExtraSalaryItems
        .filter(
          (it) =>
            (it?.key || "").toString().trim() !== "" ||
            (Number(it?.value) || 0) > 0
        )
        .forEach((it) => {
          const k = (it?.key || "").toString().trim() || "未命名";
          const v = Number(it?.value) || 0;
          map[k] = v;
        });
      this.form.company_extra_salary = map;
      this.closeCompanyExtraSalaryDialog();
    },
    startEdit() {
      this.edit = true;
      this.resetForm();
    },
    cancelEdit() {
      this.edit = false;
      this.resetForm();
    },
    submit() {
      if (this.saving) return;
      this.saving = true;

      let extraInsurance = this.form.extra_insurance;
      if (typeof extraInsurance === "string") {
        try {
          extraInsurance = JSON.parse(extraInsurance);
        } catch (e) {
          console.error("Failed to parse extra_insurance:", e);
          extraInsurance = this.extraInsuranceForm;
        }
      }

      if (extraInsurance && typeof extraInsurance === "object") {
        delete extraInsurance.employee_voluntary_pension;
        delete extraInsurance.dependent_health_insurance;
        delete extraInsurance.dependent_health_insurance_items;
      }

      const insuranceBracketMaxRaw = this.form.insurance_bracket_max;
      const insuranceBracketMax =
        insuranceBracketMaxRaw == null || insuranceBracketMaxRaw === ""
          ? null
          : Number(insuranceBracketMaxRaw);

      const payload = {
        profile: {
          basic_salary: this.form.basic_salary,
          extra_salary: this.form.extra_salary,
          salary_settle_freq: this.form.salary_settle_freq,
          salary_settle_date: this.form.salary_settle_date,
          salary_pay_freq: this.form.salary_pay_freq,
          salary_pay_date: this.form.salary_pay_date,
          salary_account: this.form.salary_account,
          note: this.form.note,
          bank: this.form.bank,
          branch: this.form.branch,
          supervisor_allowance: this.form.supervisor_allowance,
          position_allowance: this.form.position_allowance,
          food_allowance: this.form.food_allowance,
          company_extra_salary: this.form.company_extra_salary,
          employee_welfare_fund: this.form.employee_welfare_fund,
          bonus: this.form.bonus,
          employee_voluntary_pension: this.form.employee_voluntary_pension,
          employee_voluntary_pension_rate:
            this.form.employee_voluntary_pension_rate,
          dependent_insured_count: this.form.dependent_insured_count,
          insurance_bracket_max:
            insuranceBracketMax != null && Number.isFinite(insuranceBracketMax)
              ? insuranceBracketMax
              : null,
          extra_insurance: extraInsurance,
        },
      };
      this.$emit("save", payload, (ok) => {
        this.saving = false;
        if (ok) this.edit = false;
      });
    },
    showHistory() {
      this.$emit("show-history", "salary");
    },
  },
};
</script>

<style>
.bracket-option-item.bracket-option-selected {
  background-color: #e8f5e9 !important;
  color: #1b5e20 !important;
}

.bracket-option-item.bracket-option-selected .v-list-item__title {
  color: #1b5e20 !important;
  font-weight: 700;
}
</style>
